import { useQuery } from '@tanstack/react-query';
import { IEmbedV2Response, IEmbedV3, IEmbedV4 } from '@opengov/reporting-types';
import { getV2Embed, getV3Embed, getV4Embed } from '@opengov/reporting-embeds';

import { useToast } from '../../../../stories/components/Toast/context/useToast.context';
import { embedPickerQueryKeys } from './embedPickerQueryKeys';

export type ClonedEmbedVersion = 'v2' | 'v3' | 'v4';

export type ClonedEmbedResult = {
  embed: IEmbedV2Response | IEmbedV3 | IEmbedV4;
  // v2-only fields extracted from embed.data.sdk.componentProps — becomes local
  // state in the container so the visualisation picker can pre-populate.
  groupDataBy: string;
  tableColumns: string[];
};

export type UseClonedEmbedParams = {
  embedId: string;
  embedVersion?: ClonedEmbedVersion;
} | null;


export function useClonedEmbed(params: UseClonedEmbedParams) {
  const { toggleToast } = useToast();

  return useQuery<ClonedEmbedResult, Error>({
    queryKey: params
      ? [...embedPickerQueryKeys.embed({ embedId: params.embedId }), 'cloned', params.embedVersion ?? 'v2']
      : ([...embedPickerQueryKeys.all, 'embed', 'cloned', 'disabled'] as const),
    queryFn: async (): Promise<ClonedEmbedResult> => {
      if (!params) throw new Error('Disabled');
      const { embedId, embedVersion } = params;

      if (embedVersion === 'v4') {
        const embed = await getV4Embed(embedId);
        return { embed, groupDataBy: 'category', tableColumns: [] };
      }

      if (embedVersion === 'v3') {
        const embed = await getV3Embed(embedId);
        return { embed, groupDataBy: 'category', tableColumns: [] };
      }

      // v2 path: extract groupDataBy / tableColumns from data.sdk.componentProps
      const embed = await getV2Embed(embedId);
      let groupDataBy = 'category';
      let tableColumns: string[] = [];

      const componentProps = (embed as any)?.data?.sdk?.componentProps;
      if (componentProps?.group_data_by && componentProps?.table_columns) {
        groupDataBy = componentProps.group_data_by;
        tableColumns = componentProps.table_columns;
      }

      return { embed, groupDataBy, tableColumns };
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: 'Could not load embed details', severity: 'error' });
    }
  });
}
